import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function TermsConditions() {
  return (
    <div className="pt-24 pb-20 bg-white min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#14B8A6] font-body mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <h1 data-testid="terms-conditions-title" className="font-heading text-3xl md:text-4xl font-bold text-[#0F172A] mb-8">Terms &amp; Conditions</h1>
        <p className="text-sm text-[#64748B] font-body mb-8">Last updated: December 2025</p>

        <div className="prose prose-slate max-w-none font-body space-y-6 text-[#475569] text-sm leading-relaxed">
          <section>
            <h2 className="font-heading text-xl font-semibold text-[#0F172A] mt-8 mb-3">1. Definitions</h2>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li><strong>"Company"</strong> refers to Carecrony</li>
              <li><strong>"Client / Care Receiver"</strong> refers to any individual or entity availing services</li>
              <li><strong>"Caregiver"</strong> refers to attendants, nurses, or nannies listed or provided through Carecrony</li>
              <li><strong>"Services"</strong> means home care, elderly care, patient care, nursing, and childcare services</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold text-[#0F172A] mt-8 mb-3">2. Nature of Relationship</h2>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Carecrony operates as a service facilitator and aggregator platform.</li>
              <li>Caregivers are independent service providers, not employees of Carecrony (unless explicitly stated).</li>
              <li>Carecrony provides sourcing, screening, coordination, and support services.</li>
              <li>The service relationship exists between the Client and Caregiver.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold text-[#0F172A] mt-8 mb-3">3. Non-Circumvention & Non-Solicitation (STRICT CLAUSE)</h2>
            <p>The Client expressly agrees:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Not to directly hire, employ, engage, or retain any Caregiver introduced by Carecrony outside the platform.</li>
              <li>Not to contact caregivers independently for private arrangements.</li>
              <li>Not to bypass Carecrony to avoid service fees.</li>
            </ul>
            <p className="mt-4"><strong>Validity Period:</strong> This restriction applies during the service period AND for 12 months after termination of services.</p>
            <p className="mt-4"><strong>Penalty for Breach:</strong> In case of violation, the Client shall be liable to pay liquidated damages equivalent to 6–12 months of service fees, OR ₹50,000 – ₹2,00,000 (as determined by Carecrony), whichever is higher. Carecrony reserves the right to initiate legal proceedings and recover damages and legal costs.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold text-[#0F172A] mt-8 mb-3">4. Eligibility</h2>
            <p>By using Carecrony, you confirm you are at least 18 years old, legally capable of entering into a binding agreement, and all provided information is accurate.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold text-[#0F172A] mt-8 mb-3">5. Services</h2>
            <p>Carecrony provides elderly care, patient care, nursing services, and nanny/childcare services. All services are subject to availability and assessment.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold text-[#0F172A] mt-8 mb-3">6. Booking & Payment Terms</h2>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Advance payment is mandatory.</li>
              <li>Fees include: Registration/placement fee (non-refundable) and Service fees (weekly/monthly).</li>
              <li>Pricing Factors: Skill level, working hours (12-hour/24-hour/live-in), location, and urgency.</li>
              <li>Late Payment: Delay in payment may result in immediate suspension of services and late penalties.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold text-[#0F172A] mt-8 mb-3">7. Cancellation & Refund Policy</h2>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Minimum 24–48 hours’ notice required.</li>
              <li>Registration/placement fees are strictly non-refundable.</li>
              <li>Refunds (if any) are prorated based on unused services. No refund in cases of misconduct by client or policy violations.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold text-[#0F172A] mt-8 mb-3">8. Client Responsibilities</h2>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Provide a safe, hygienic, and respectful work environment.</li>
              <li>Accurately disclose patient health conditions.</li>
              <li>Provide food and accommodation (for live-in caregivers).</li>
              <li>Ensure no illegal, abusive, or unsafe activities.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold text-[#0F172A] mt-8 mb-3">9. Caregiver Conduct & Disclaimer</h2>
            <p>Carecrony does not guarantee medical outcomes and is not responsible for negligence beyond reasonable control. Carecrony shall not be liable for theft, loss, or damages.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold text-[#0F172A] mt-8 mb-3">10. Replacement Policy</h2>
            <p>Replacement will be provided in case of caregiver unavailability or performance issues. Replacement timelines depend on availability.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold text-[#0F172A] mt-8 mb-3">11. Limitation of Liability</h2>
            <p>Carecrony’s liability shall not exceed the amount of fees paid in the last 30 days. Carecrony shall not be liable for indirect damages, medical emergencies, or death.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold text-[#0F172A] mt-8 mb-3">12. Dispute Resolution & Jurisdiction</h2>
            <p>Disputes shall first be resolved amicably. If unresolved, they shall be referred to arbitration in Pune, Maharashtra under the Arbitration and Conciliation Act, 1996. These Terms are governed by Indian laws with jurisdiction in Pune courts.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold text-[#0F172A] mt-8 mb-3">13. Contact</h2>
            <p>For questions about these terms:</p>
            <p className="mt-2">Email: <a href="mailto:support@carecrony.com" className="text-[#14B8A6] hover:underline">support@carecrony.com</a></p>
            <p>Phone: <a href="tel:+919270425109" className="text-[#14B8A6] hover:underline">+91 9270425109</a></p>
          </section>
        </div>
      </div>
    </div>
  );
}
